import { useEffect, useState } from "react";
import { getUser } from "../../helper/http.ts";
import styles from "./AxiomeBalancePage.module.css";

interface AxiomeBalance {
  id?: string;
  account_id?: string;
  type?: string;
  type_title?: string;
  currency?: string;
  available?: number | string;
  holding?: number | string;
  pending?: number | string;
  frozen?: number | string;
  disabled?: boolean;
}

interface TronWalletBalance {
  address?: string;
  ok?: boolean;
  usdt?: number | string;
  trx?: number | string;
  error?: string;
  source?: string;
  updatedAt?: string;
}

function formatAmount(value: number | string | undefined) {
  const numberValue = Number(value ?? 0);

  if (!Number.isFinite(numberValue)) {
    return String(value ?? "0");
  }

  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
}

export default function AxiomeBalancePage() {
  const [balances, setBalances] = useState<AxiomeBalance[]>([]);
  const [tronWallet, setTronWallet] = useState<TronWalletBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const loadBalances = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getUser("api/axiome/admin-balance");
      setBalances(Array.isArray(data) ? data : data?.balances || []);
      setTronWallet(Array.isArray(data) ? null : data?.tronWallet || null);
      setUpdatedAt(new Date());
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load Axiome balance",
      );
      setBalances([]);
      setTronWallet(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBalances();
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Axiome Balance</h1>
          <div className={styles.subtitle}>
            {updatedAt
              ? `Updated ${updatedAt.toLocaleString()}`
              : "Request is sent when this page opens"}
          </div>
        </div>

        <button
          type="button"
          className={styles.refreshButton}
          onClick={loadBalances}
          disabled={loading}
        >
          {loading ? "Loading" : "Refresh"}
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {tronWallet && (
        <section className={`${styles.card} ${styles.walletCard}`}>
          <div className={styles.cardHeader}>
            <div>
              <div className={styles.currency}>USDT Wallet</div>
              <div className={styles.type}>{tronWallet.address}</div>
            </div>
            <span className={tronWallet.ok ? styles.status : styles.statusDisabled}>
              {tronWallet.ok ? "Active" : "Error"}
            </span>
          </div>

          <div className={styles.amount}>{formatAmount(tronWallet.usdt)}</div>
          <div className={styles.amountLabel}>USDT Available</div>

          <div className={styles.metrics}>
            <div>
              <span>TRX</span>
              <strong>{formatAmount(tronWallet.trx)}</strong>
            </div>
            <div>
              <span>Source</span>
              <strong>{tronWallet.source || "tronscan"}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{tronWallet.error || (tronWallet.ok ? "OK" : "Unavailable")}</strong>
            </div>
          </div>
        </section>
      )}

      {loading && !balances.length ? (
        <div className={styles.card}>
          <div className={styles.loadingLine} />
          <div className={styles.loadingLineSmall} />
        </div>
      ) : balances.length ? (
        <div className={styles.grid}>
          {balances.map((balance) => (
            <section
              key={balance.id || `${balance.currency}-${balance.type}`}
              className={styles.card}
            >
              <div className={styles.cardHeader}>
                <div>
                  <div className={styles.currency}>
                    {balance.currency || "Unknown"}
                  </div>
                  <div className={styles.type}>
                    {balance.type_title || balance.type || "Balance"}
                  </div>
                </div>
                <span
                  className={
                    balance.disabled ? styles.statusDisabled : styles.status
                  }
                >
                  {balance.disabled ? "Disabled" : "Active"}
                </span>
              </div>

              <div className={styles.amount}>
                {formatAmount(balance.available)}
              </div>
              <div className={styles.amountLabel}>Available</div>

              <div className={styles.metrics}>
                <div>
                  <span>Holding</span>
                  <strong>{formatAmount(balance.holding)}</strong>
                </div>
                <div>
                  <span>Pending</span>
                  <strong>{formatAmount(balance.pending)}</strong>
                </div>
                <div>
                  <span>Frozen</span>
                  <strong>{formatAmount(balance.frozen)}</strong>
                </div>
              </div>

              <pre className={styles.rawJson}>
                {JSON.stringify(balance, null, 2)}
              </pre>
            </section>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>No balances returned by Axiome.</div>
      )}
    </div>
  );
}
